import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ServiceWorkerUpdateService } from '@animus/utility/web/service-worker';
import { injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmDialogDescription, HlmDialogTitle } from '@spartan-ng/helm/dialog';

interface UpdateAvailableDialogContext {
    close: () => void;
}

/** Explains that a new app version is downloaded and offers to activate it immediately. */
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [HlmButtonImports, HlmDialogDescription, HlmDialogTitle],
    selector: 'app-update-available-dialog',
    template: `
        <div class="min-w-0 pr-8">
            <h2 class="text-lg font-semibold" hlmDialogTitle>Update available</h2>
            <p class="mt-1 text-sm text-muted-foreground" hlmDialogDescription>
                A new version of The Animus is ready.
            </p>
        </div>

        <div class="mt-4 text-sm leading-6 text-foreground">
            <p>
                A new version of the app has finished downloading. Activate it now to pick up the latest rules,
                fixes, and features. Your current session will close and the app will reload.
            </p>
        </div>

        <div class="mt-4 flex flex-col justify-end gap-2 sm:flex-row">
            <button (click)="update()" hlmBtn variant="default" type="button">Update now</button>
            <button (click)="context.close()" hlmBtn variant="outline" type="button">Later</button>
        </div>
    `,
})
export class UpdateAvailableDialog {
    protected readonly context = injectBrnDialogContext<UpdateAvailableDialogContext>();

    private readonly updateService = inject(ServiceWorkerUpdateService);

    protected update(): void {
        this.context.close();
        this.updateService.activateUpdate();
    }
}
